import { treeHasPostprocess } from "../utils/layerTree";
import {
  isCarouselLayout,
  listAllCells,
  normalizeFormatIDs,
} from "../utils/slides";
import {
  CONTENT_LANGUAGE_PRESET,
  ensureContentLanguageField,
} from "../../../utils/catalogs/contentLanguage";
import {
  isHighlightPhraseInText,
  listHighlightColumns,
} from "../utils/textHighlights";
import { buildProduceVariableKey } from "./produceVariableKeys";
import { countProducePendingGalleryItems } from "./produceGalleryItems";

const originExampleFromNode = ({ node, assets = {} }) => {
  if (node?.type === "image") {
    const ref = String(node.originAssetRef || "").trim();
    return String(assets[ref]?.url || "").trim();
  }
  return String(node?.originText || "").trim();
};

export const listProduceColumns = ({
  variables = {},
  nodes = {},
  artboards = {},
  slides = [],
  layoutKind,
  assets = {},
  slideIDs,
}) => {
  const isCarousel = isCarouselLayout({ layoutKind, slides });
  const used = {};
  const highlightsByParent = {};
  const walk = ({ map, slideID = "", slideIndex } = {}) => {
    Object.values(map || {}).forEach((node) => {
      const variable =
        node?.source?.kind === "data"
          ? String(node.source.variable || "").trim()
          : "";
      const key = buildProduceVariableKey({
        slideIndex,
        variable,
        isCarousel,
      });
      if (key && !used[key]) {
        used[key] = {
          key,
          label: slideID ? key : node.name || variables[variable]?.label || key,
          type:
            variables[variable]?.type === "image" || node.type === "image"
              ? "image"
              : "string",
          originExample: originExampleFromNode({ node, assets }),
          slideID,
          slideIndex,
          variable,
          nodeID: node.id || "",
        };
      } else if (key && used[key] && !used[key].originExample) {
        used[key].originExample = originExampleFromNode({ node, assets });
      }
      if (key && node?.type === "text" && !highlightsByParent[key]) {
        const extras = listHighlightColumns({
          variable: key,
          highlights: node.style?.highlights,
          text: originExampleFromNode({ node, assets }),
        });
        if (extras.length) highlightsByParent[key] = extras;
      }
      if (node.children) {
        walk({ map: node.children, slideID, slideIndex });
      }
    });
  };
  const cells = listAllCells({ layoutKind, slides, artboards, slideIDs });
  cells.forEach((cell) => {
    if (!isCarousel) {
      walk({ map: cell?.artboard?.nodes || {} });
      return;
    }
    const resolvedIndex = (slides || []).findIndex(
      (slide) => slide?.id === cell?.slideID
    );
    walk({
      map: cell?.artboard?.nodes || {},
      slideID: cell?.slideID || "",
      slideIndex: resolvedIndex >= 0 ? resolvedIndex : (cell?.slideIndex ?? 0),
    });
  });
  if (!isCarousel) {
    walk({ map: nodes });
  }
  return Object.values(used)
    .sort((left, right) => {
      const slideDiff = (left.slideIndex ?? 0) - (right.slideIndex ?? 0);
      if (slideDiff !== 0) return slideDiff;
      return left.key.localeCompare(right.key);
    })
    .flatMap((column) => [column, ...(highlightsByParent[column.key] || [])]);
};

// Detecta columnas renombradas (mismo nodeID, distinta key) y devuelve
// { [keyAnterior]: keyNueva } para migrar los valores ya escritos en filas.
export const getRenamedColumnKeys = ({
  previousColumns = [],
  nextColumns = [],
}) => {
  const nextByNodeID = {};
  nextColumns.forEach((column) => {
    if (column.nodeID && !nextByNodeID[column.nodeID]) {
      nextByNodeID[column.nodeID] = column;
    }
  });
  const nextKeys = new Set(nextColumns.map((column) => column.key));
  const renames = {};
  previousColumns.forEach((column) => {
    if (!column.nodeID || nextKeys.has(column.key)) return;
    const target = nextByNodeID[column.nodeID];
    if (target && target.key !== column.key) renames[column.key] = target.key;
  });
  return renames;
};

export const createEmptyRow = ({ columns = [] }) => {
  const row = {};
  columns.forEach((column) => {
    row[column.key] = "";
  });
  return row;
};

export const PRODUCE_MIN_VARIATION_COUNT = 1;
export const PRODUCE_MAX_ROWS = 200;

const resolveProduceRowCap = (max) => {
  const cap = Number(max);
  if (!Number.isInteger(cap) || cap < PRODUCE_MIN_VARIATION_COUNT) {
    return PRODUCE_MAX_ROWS;
  }
  return Math.min(cap, PRODUCE_MAX_ROWS);
};

export const sanitizeProduceVariationCountInput = ({
  value,
  max = PRODUCE_MAX_ROWS,
} = {}) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .replace(/^0+/, "");
  if (!digits) return "";
  const parsed = Number(digits);
  if (!Number.isInteger(parsed)) return "";
  const cap = resolveProduceRowCap(max);
  if (parsed > cap) return String(cap);
  return digits;
};

export const parseProduceVariationCount = ({ value } = {}) => {
  const parsed = Number(sanitizeProduceVariationCountInput({ value }));
  if (!Number.isInteger(parsed) || parsed < PRODUCE_MIN_VARIATION_COUNT) {
    return 0;
  }
  return parsed;
};

export const isValidProduceVariationCount = ({ value } = {}) => {
  const parsed = parseProduceVariationCount({ value });
  return parsed >= PRODUCE_MIN_VARIATION_COUNT;
};

export const clampProduceVariationCount = ({
  value,
  max = PRODUCE_MAX_ROWS,
} = {}) => {
  const parsed = parseProduceVariationCount({ value });
  if (parsed < PRODUCE_MIN_VARIATION_COUNT) return PRODUCE_MIN_VARIATION_COUNT;
  const cap = resolveProduceRowCap(max);
  if (parsed > cap) return cap;
  return parsed;
};

export const createVariationRows = ({ count } = {}) => {
  const parsed = clampProduceVariationCount({ value: count });
  return Array.from({ length: parsed }, (_, index) => ({
    variationIndex: String(index + 1),
  }));
};

export const getProduceProductionRowCount = ({
  rowCount = 0,
  variationCount,
  hasVariables = false,
} = {}) => {
  const rows = Math.max(0, Number(rowCount) || 0);
  if (!hasVariables) return rows;
  return rows * parseProduceVariationCount({ value: variationCount });
};

export const getProduceVariationsPerRow = ({
  variationCount,
  hasVariables = false,
} = {}) => {
  if (!hasVariables) return 1;
  const parsed = parseProduceVariationCount({ value: variationCount });
  return parsed > 0 ? parsed : 1;
};

export const isImageHttpUrl = ({ value }) =>
  /^https?:\/\//i.test(String(value || "").trim());

const SKIP_PREVIEW_ROW_KEYS = new Set(["variationIndex"]);

const listAsArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => value[key])
      .filter(Boolean);
  }
  return [];
};

export const serializeProduceColumns = ({ columns = [] } = {}) =>
  listAsArray(columns)
    .filter((column) => column?.key)
    .map((column) => {
      const next = {
        key: String(column.key),
        label: String(column.label || column.key),
        type: column.type === "image" ? "image" : "string",
      };
      if (column.role === "highlight") next.role = "highlight";
      if (column.nodeID) next.nodeID = String(column.nodeID);
      return next;
    });

export const listPreviewVariableEntries = ({ rows, columns } = {}) => {
  const columnList = serializeProduceColumns({ columns });
  const row = listAsArray(rows)[0] || {};
  if (columnList.length > 0) {
    return columnList.map((column) => ({
      ...column,
      value: row?.[column.key] ?? "",
    }));
  }
  return Object.keys(row)
    .filter((key) => key && !SKIP_PREVIEW_ROW_KEYS.has(key))
    .map((key) => {
      const value = row[key] ?? "";
      return {
        key,
        label: key,
        type: isImageHttpUrl({ value }) ? "image" : "string",
        value,
      };
    });
};

export const listProduceImageColumnKeys = ({ columns = [] } = {}) =>
  columns
    .filter((column) => column?.type === "image")
    .map((column) => column.key)
    .filter(Boolean);

export const collectProduceImageUrls = ({ rows = [], columns = [] } = {}) => {
  const imageKeys = listProduceImageColumnKeys({ columns });
  const urls = new Set();
  rows.forEach((row) => {
    imageKeys.forEach((key) => {
      const url = String(row?.[key] || "").trim();
      if (isImageHttpUrl({ value: url })) urls.add(url);
    });
  });
  return [...urls];
};

export const rewriteProduceRowsWithUrlMap = ({
  rows = [],
  columns = [],
  urlMap = {},
} = {}) => {
  const imageKeys = listProduceImageColumnKeys({ columns });
  return rows.map((row) => {
    const next = { ...row };
    imageKeys.forEach((key) => {
      const current = String(row?.[key] || "").trim();
      if (urlMap[current]) next[key] = urlMap[current];
    });
    return next;
  });
};

export const getProduceImageCellStatus = ({
  url,
  checkingUrls,
  failedUrls,
} = {}) => {
  const value = String(url || "").trim();
  if (value && checkingUrls?.has?.(value)) return "checking";
  if (value && failedUrls?.has?.(value)) return "error";
  return "ok";
};

export const getFailedProduceImageRowIndexes = ({
  rows = [],
  columns = [],
  failedUrls,
} = {}) => {
  if (!failedUrls?.size) return [];
  const imageKeys = listProduceImageColumnKeys({ columns });
  return rows
    .map((row, index) => {
      const hasFailed = imageKeys.some((key) =>
        failedUrls.has(String(row?.[key] || "").trim())
      );
      return hasFailed ? index : -1;
    })
    .filter((index) => index >= 0);
};

export const rowsHaveFailedProduceImages = ({
  rows = [],
  columns = [],
  failedUrls,
} = {}) =>
  getFailedProduceImageRowIndexes({ rows, columns, failedUrls }).length > 0;

export const isCellFilled = ({ value, type }) => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (type === "image") return isImageHttpUrl({ value: text });
  return true;
};

const isColumnValueValid = ({ value, column, row }) => {
  if (!isCellFilled({ value, type: column?.type })) return false;
  if (column?.role !== "highlight") return true;
  return isHighlightPhraseInText({
    text: row?.[column.parentKey],
    phrase: value,
  });
};

export const isRowEmpty = ({ row = {}, columns = [] }) =>
  columns.every((column) => !String(row?.[column.key] || "").trim());

export const hasNonEmptyRows = ({ rows = [], columns = [] }) =>
  rows.some((row) => !isRowEmpty({ row, columns }));

export const getInvalidProduceCellKeysByRow = ({
  rows = [],
  columns = [],
} = {}) => {
  const byRow = {};
  rows.forEach((row, index) => {
    const keys = columns
      .filter(
        (column) =>
          !isColumnValueValid({
            value: row?.[column.key],
            column,
            row,
          })
      )
      .map((column) => column.key);
    if (keys.length) byRow[index] = keys;
  });
  return byRow;
};

export const getInvalidRowIndexes = ({ rows = [], columns = [] }) =>
  Object.keys(getInvalidProduceCellKeysByRow({ rows, columns })).map(Number);

export const isProduceRowComplete = ({ row = {}, columns = [] }) =>
  columns.every((column) =>
    isColumnValueValid({
      value: row?.[column.key],
      column,
      row,
    })
  );

export const hasInvalidHighlightPhrase = ({ rows = [], columns = [] } = {}) =>
  rows.some((row) =>
    columns.some((column) => {
      if (column?.role !== "highlight") return false;
      const value = row?.[column.key];
      return (
        isCellFilled({ value, type: column.type }) &&
        !isHighlightPhraseInText({
          text: row?.[column.parentKey],
          phrase: value,
        })
      );
    })
  );

export const listPreviewProduceRows = ({
  rows = [],
  columns = [],
  failedUrls,
} = {}) => {
  const failedIndexes = new Set(
    getFailedProduceImageRowIndexes({ rows, columns, failedUrls })
  );
  return rows.filter(
    (row, index) =>
      isProduceRowComplete({ row, columns }) && !failedIndexes.has(index)
  );
};

export const pickRandomItem = ({ items = [] } = {}) => {
  if (!items.length) return null;
  return items[Math.floor(Math.random() * items.length)];
};

export const getDefaultProduceLanguage = ({ language } = {}) =>
  ensureContentLanguageField({
    value: language,
    storedLanguage: language,
    preset: CONTENT_LANGUAGE_PRESET.TEMPLATES,
    platformLanguage: language,
  });

export const scrollToProduceRow = ({ rowRefs, index }) => {
  const node = rowRefs?.current?.[index];
  if (!node || typeof node.scrollIntoView !== "function") return;
  node.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const scrollToProduceResults = ({ resultsRef }) => {
  const node = resultsRef?.current;
  if (!node || typeof node.scrollIntoView !== "function") return;
  node.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const normalizeStoredRows = ({ rows } = {}) => {
  if (Array.isArray(rows)) return rows;
  if (rows && typeof rows === "object") {
    return Object.keys(rows)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => rows[key])
      .filter((row) => row && typeof row === "object");
  }
  return [];
};

// Adapta las filas de una producción anterior a las columnas actuales.
// Empata por nodeID (sobrevive a renombres) y, si no hay, por key.
// Descarta filas que queden vacías; devuelve [] si no hay nada importable.
export const mapStoredRowsToColumns = ({
  storedRows,
  storedColumns,
  columns = [],
} = {}) => {
  const rows = normalizeStoredRows({ rows: storedRows });
  if (rows.length === 0 || columns.length === 0) return [];
  const storedList = listAsArray(storedColumns);
  const storedKeyByNodeID = {};
  storedList.forEach((column) => {
    if (column?.nodeID && column?.key && !storedKeyByNodeID[column.nodeID]) {
      storedKeyByNodeID[column.nodeID] = column.key;
    }
  });
  const sourceKeys = columns.map(
    (column) =>
      (column.nodeID && storedKeyByNodeID[column.nodeID]) || column.key
  );
  return rows
    .map((row) => {
      const next = createEmptyRow({ columns });
      columns.forEach((column, index) => {
        const value = row?.[sourceKeys[index]];
        if (value == null || typeof value === "object") return;
        next[column.key] = String(value);
      });
      return next;
    })
    .filter((row) => !isRowEmpty({ row, columns }))
    .slice(0, PRODUCE_MAX_ROWS);
};

export const normalizeStoredIDs = ({ value } = {}) =>
  normalizeFormatIDs({ value });

export const resolveSelectedArtboardIDs = ({
  availableIDs = [],
  selectedIDs = [],
  storedIDs = [],
} = {}) => {
  if (!availableIDs.length) return [];
  const storedFormatIDs = normalizeFormatIDs({ value: storedIDs });
  if (storedFormatIDs.length > 0) {
    const storedSet = new Set(storedFormatIDs);
    return availableIDs.filter((id) => storedSet.has(id));
  }
  const selectedSet = new Set(normalizeFormatIDs({ value: selectedIDs }));
  const next = availableIDs.filter((id) => selectedSet.has(id));
  return next.length > 0 ? next : [...availableIDs];
};

export const toggleSelectedID = ({
  availableIDs = [],
  selectedIDs = [],
  id,
} = {}) => {
  if (!id || !availableIDs.includes(id)) return selectedIDs;
  const selectedSet = new Set(selectedIDs);
  if (selectedSet.has(id)) {
    if (selectedSet.size <= 1) return selectedIDs;
    selectedSet.delete(id);
  } else {
    selectedSet.add(id);
  }
  return availableIDs.filter((availableID) => selectedSet.has(availableID));
};

export const PRODUCE_RUN_ACTIVE_STATUSES = new Set([
  "queued",
  "processing",
  "prepared",
  "retrying",
]);

export const getProduceProgress = ({ produceRecord } = {}) => {
  const progress = produceRecord?.progress || {};
  return {
    requested: Number(progress.requested) || 0,
    queued: Number(progress.queued) || 0,
    processing: Number(progress.processing) || 0,
    retrying: Number(progress.retrying) || 0,
    succeeded: Number(progress.succeeded) || 0,
    failed: Number(progress.failed) || 0,
    cancelled: Number(progress.cancelled) || 0,
  };
};

export const getProducePendingCount = ({
  produceRecord,
  isStartingProduce = false,
  expectedCreativeCount = 0,
  slidesPerItem = 1,
} = {}) => {
  const progress = getProduceProgress({ produceRecord });
  const inFlight = progress.queued + progress.processing + progress.retrying;
  const completedCount = Object.values(produceRecord?.creatives || {}).filter(
    (item) => item?.url
  ).length;
  return countProducePendingGalleryItems({
    inFlight,
    expectedCreativeCount,
    completedCount,
    isStartingProduce:
      isStartingProduce ||
      (produceRecord?.status === "processing" && inFlight === 0),
    slidesPerItem,
  });
};

export const getProduceSummary = ({ produceRecord } = {}) => {
  const progress = getProduceProgress({ produceRecord });
  const summary = produceRecord?.summary || {};
  return {
    requested: Number(summary.requested) || progress.requested || 0,
    succeeded: Number(summary.succeeded) || progress.succeeded || 0,
    failed: Number(summary.failed) || progress.failed || 0,
    cancelled: Number(summary.cancelled) || progress.cancelled || 0,
  };
};

export const getLatestProduceRun = ({ produceRecord } = {}) => {
  const runs = produceRecord?.runs || {};
  const preferredID = produceRecord?.activeRunID || produceRecord?.jobID;
  if (preferredID && runs[preferredID]) {
    return { ...runs[preferredID], jobID: preferredID };
  }
  return (
    Object.entries(runs)
      .map(([jobID, run]) => ({ ...run, jobID }))
      .sort(
        (left, right) => (right.createdAt || 0) - (left.createdAt || 0)
      )[0] || null
  );
};

export const classifyProduceFailureReason = ({ error } = {}) => {
  const message = String(error || "").toLowerCase();
  if (
    message.includes("rate") ||
    message.includes("429") ||
    message.includes("busy") ||
    message.includes("timeout") ||
    message.includes("unavailable")
  ) {
    return "busy";
  }
  if (
    message.includes("url") ||
    message.includes("image") ||
    message.includes("download") ||
    message.includes("expired") ||
    message.includes("accessible")
  ) {
    return "image";
  }
  if (
    message.includes("text") ||
    message.includes("missing") ||
    message.includes("empty") ||
    message.includes("parameter")
  ) {
    return "data";
  }
  return "generic";
};

export const listProduceFailedSlots = ({ produceRecord } = {}) => {
  const run = getLatestProduceRun({ produceRecord });
  const slots = [];
  Object.entries(run?.rows || {}).forEach(([rowIndex, row]) => {
    Object.values(row?.formats || {}).forEach((format) => {
      if (format?.status !== "error") return;
      const artboardID = String(format.artboardID || "").trim();
      slots.push({
        id: `fail-${run.jobID}-${rowIndex}-${artboardID || "primary"}`,
        jobID: run.jobID || null,
        productionRowIndex: Number(rowIndex),
        artboardID: artboardID || null,
        slideID: format.slideID || null,
        slideIndex: Number(format.slideIndex) || 0,
        error: format.error || null,
        reason: classifyProduceFailureReason({ error: format.error }),
        sourceRowIndex: row.sourceRowIndex,
        variationIndex: row.variationIndex,
      });
    });
  });
  if (slots.length > 0) return slots;
  const failed = getProduceSummary({ produceRecord }).failed;
  return Array.from({ length: failed }, (_, index) => ({
    id: `fail-summary-${index}`,
    productionRowIndex: index,
    artboardID: null,
    error: null,
    reason: "generic",
  }));
};

export const hasActiveProduceRun = ({ produceRecord } = {}) => {
  if (produceRecord?.status === "processing") return true;
  const progress = getProduceProgress({ produceRecord });
  if (progress.queued > 0 || progress.processing > 0 || progress.retrying > 0) {
    return true;
  }
  return Object.values(produceRecord?.runs || {}).some((run) =>
    PRODUCE_RUN_ACTIVE_STATUSES.has(run?.status)
  );
};

export const listProduceCreatives = ({ creatives } = {}) =>
  Object.values(creatives || {})
    .filter((item) => item?.url)
    .sort(
      (left, right) =>
        (left.rowIndex ?? 0) - (right.rowIndex ?? 0) ||
        (left.slideIndex ?? 0) - (right.slideIndex ?? 0) ||
        String(left.artboardID || "").localeCompare(
          String(right.artboardID || "")
        ) ||
        (right.creationTime ?? 0) - (left.creationTime ?? 0)
    )
    .map((item) => ({
      id: item.id,
      url: item.url,
      type: item.type || "upload",
      width: item.width,
      height: item.height,
      artboardID: item.artboardID || null,
      slideID: item.slideID || null,
      slideIndex: item.slideIndex ?? 0,
      rowIndex: item.rowIndex,
      jobID: item.jobID || null,
      instance: item.instance || null,
      postprocessed: Boolean(item.postprocessed),
      creationTime: item.creationTime,
    }));

export const produceItemNeedsLayerReanalysis = ({
  item,
  currentImageUrl,
} = {}) => {
  const originalUrl = String(item?.url || "").trim();
  const imageUrl = String(currentImageUrl || originalUrl).trim();
  if (imageUrl && originalUrl && imageUrl !== originalUrl) return true;
  if (Boolean(item?.postprocessed)) return true;
  return treeHasPostprocess({ nodes: item?.instance?.nodes });
};
